import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  SearchOutlined,
  FormatListBulletedOutlined,
  ShoppingCartOutlined,
  SellOutlined,
  SyncAltOutlined,
  VolunteerActivismOutlined,
  CheckBoxOutlined,
} from "@mui/icons-material";

const HowR3playWorks = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cards = [
    {
      icon: SearchOutlined,
      title: "Discover",
      description:
        "Browse a world of toys curated by people who genuinely care.",
      additionalText:
        "From vintage treasures to the latest collectibles, everything is organized to help you find exactly what sparks joy.",
      subtitle: "Tools you'll love:",
      items: [
        { text: "Smart search", hasCheckbox: true },
        { text: "Personalized feeds", hasCheckbox: true },
        { text: "Collector categories", hasCheckbox: true },
        { text: "Story-rich listings", hasCheckbox: true },
      ],
    },
    {
      icon: FormatListBulletedOutlined,
      title: "List",
      description:
        "Listing a toy is as easy as snapping a few photos and telling the story behind it — because on R3PLAY, the story matters as much as the item.",
      additionalText:
        "From vintage treasures to the latest collectibles, everything is organized to help you find exactly what sparks joy.",
      subtitle: "You can:",
      items: [
        { text: "Set your price", hasCheckbox: true },
        { text: "Mark it as trade-only", hasCheckbox: true },
        { text: "Choose donation instead of resale", hasCheckbox: true },
        { text: "Add its memory or origin story", hasCheckbox: true },
        { text: "Tag its condition, rarity, or collection", hasCheckbox: true },
      ],
      footerText:
        "Your listing becomes part of the R3PLAY universe of joyful discovery.",
    },
    {
      icon: ShoppingCartOutlined,
      title: "Buy",
      description:
        "Found something that sparks that oh-my-god-I-had-this feeling?",
      additionalText: "Buy it directly, securely, and easily.",
      subtitle: "What buyers get:",
      items: [
        { text: "Transparent pricing", hasCheckbox: true },
        { text: "Safe checkout", hasCheckbox: true },
        { text: "Optional story with each toy", hasCheckbox: true },
      ],
    },
    {
      icon: SellOutlined,
      title: "Sell",
      description: "Give your toys a second chapter while earning money.",
      subtitle: "What sellers get:",
      items: [
        { text: "Simple listing tools", hasCheckbox: true },
        { text: "Messaging built for community vibes", hasCheckbox: true },
        { text: "Seller stats & reviews", hasCheckbox: true },
        {
          text: "A place where toys are valued, not discarded",
          hasCheckbox: true,
        },
      ],
      footerText:
        "R3PLAY takes the complexity out of resale so you can focus on joy.",
    },
    {
      icon: SyncAltOutlined,
      title: "Trade",
      description: "Want a swap instead of a sale?",
      additionalText:
        "Trading is built into the platform so collectors, families, and kidults can exchange toys without friction.",
      subtitle: "Trade features:",
      items: [
        { text: "Trade-only listings", hasCheckbox: true },
        { text: "Offer/Counter-offer flow", hasCheckbox: true },
      ],
      footerText: "Trading brings the fun back into collecting.",
    },
    {
      icon: VolunteerActivismOutlined,
      title: "Donate",
      description:
        "Sometimes a toy is meant to move forward without a price tag.",
      additionalText:
        "R3PLAY partners with community organizations to make it easy to donate toys with intention.",
      subtitle: "You can:",
      items: [
        { text: "Choose donation at listing", hasCheckbox: true },
        { text: "Select from curated charity partners", hasCheckbox: true },
        { text: "Include a message or story", hasCheckbox: true },
      ],
      footerText: "This is how play becomes impact.",
    },
    {
      icon: VolunteerActivismOutlined,
      title: "Connect",
      description: "R3PLAY is more than a marketplace — it's a community.",
      additionalText:
        "Share your collections, follow other collectors, and celebrate the stories that make toys meaningful.",
      subtitle: "Community features:",
      items: [
        { text: "Profiles & collections", hasCheckbox: true },
        { text: "Follower feeds", hasCheckbox: true },
        { text: "Story threads", hasCheckbox: true },
        { text: "Messaging & comments", hasCheckbox: true },
      ],
      footerText:
        "This is where nostalgia, technology, and community come to play — together.",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        minHeight: "100vh",
        px: "100px",
        "@media (max-width: 1360px)": {
          px: "16px",
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "1240px",
          py: 6,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            textAlign: "center",
            mb: 2,
            fontWeight: "bold",
            fontSize: "36px",
            "@media (max-width: 833px)": {
              fontSize: "30px",
            },
          }}
        >
          How R3PLAY Works
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            mb: 6,
            fontSize: "16px",
            fontWeight: 600,
            color: "#666",
            maxWidth: "900px",
            mx: "auto",
          }}
        >
          R3PLAY is designed to make buying, selling, trading, and donating toys
          feel simple, fun, and meaningful — the way play should be.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "24px",
            justifyContent: "flex-start",
          }}
        >
          {cards.map((card, index) => {
            const IconComponent = card.icon;
            return (
              <Box
                key={index}
                sx={{
                  width: "397px",
                  height: "560px",
                  "@media (max-width: 1360px)": {
                    width: "387px",
                  },
                  "@media (max-width: 833px)": {
                    width: "361px",
                    height: "auto",
                  },
                  border: "1px solid #E0E0E0",
                  borderRadius: "12px",
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    backgroundColor: "#FFF1F4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <IconComponent sx={{ fontSize: 32, color: "#E91E63" }} />
                </Box>

                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    textAlign: "center",
                    mb: 1,
                    color: "#333",
                  }}
                >
                  {card.title}
                </Typography>

                {card.description && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      textAlign: "center",
                      mb: 2,
                      color: "#666",
                      lineHeight: 1.4,
                    }}
                  >
                    {card.description}
                  </Typography>
                )}

                {card.additionalText && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      textAlign: "center",
                      mb: 2,
                      color: "#666",
                      lineHeight: 1.4,
                    }}
                  >
                    {card.additionalText}
                  </Typography>
                )}

                <Box sx={{ width: "100%", flex: 1 }}>
                  {card.subtitle && (
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        mb: 1,
                        color: "#333",
                      }}
                    >
                      {card.subtitle}
                    </Typography>
                  )}

                  {card.items.map((item, itemIndex) => (
                    <Box
                      key={itemIndex}
                      sx={{
                        mb: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      {item.hasCheckbox && (
                        <CheckBoxOutlined
                          sx={{
                            width: "18px",
                            height: "18px",
                            color: "#04A15B",
                            flexShrink: 0,
                            mt: 0.2,
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  ))}

                  {card.footerText && (
                    <Typography
                      sx={{
                        fontSize: "14px",
                        textAlign: "center",
                        mt: 2,
                        color: "#666",
                        lineHeight: 1.4,
                      }}
                    >
                      {card.footerText}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default HowR3playWorks;
